import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { getCrops, loadCrops } from '@next/arable-lib/crop';
import { getArableAdviceCropFields, loadArableAdviceCropFields } from '@next/arable-lib/crop-field';
import { filterLoaded } from '@next/arable-lib/external-resource';
import { CropFieldType } from '@next/arable-lib/shared';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, merge, of, skip, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { loadSoilTypesInitiate, SoilTypeSelectors } from 'src/app/soil-type';

import { AppState, ManualCrop } from '../../../shared/models';

import { deleteCropField, loadCropVarietiesByCropIdRequest, upsertCropField } from '../../store/actions';

import { selectCropVarietiesByCropId } from '../../store/selectors';
import { selectCropYear } from '../../../store/selectors';
import { CropYearService } from '../../../shared/services/crop-year.service';

import { AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ManualCropFormComponent } from '../manual-crop-form/manual-crop-form.component';

@Component({
  selector: 'app-crops',
  templateUrl: './manual-crops.component.html',
  styleUrls: ['./manual-crops.component.scss'],
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatCard,
    MatCardContent,
    MatPaginator,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ManualCropsComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(DialogService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  hasUnsavedData = false;
  hasNoCrops$ = of(true);
  existingCropFields$ = this.store.select(getArableAdviceCropFields);
  crops$ = this.store.select(getCrops);
  soilTypes$ = this.store.select(SoilTypeSelectors.select);
  cropVarieties$ = this.store.select(selectCropVarietiesByCropId);
  manualCrops: ManualCrop[] = [];
  destroy$ = new Subject<boolean>();

  private readonly isNexusLoadingTrigger$ = new Subject<void>();

  isNexusLoading$ = merge(
    this.isNexusLoadingTrigger$.pipe(
      switchMap(() => merge(
        of(true),
        timer(5_000).pipe(map(() => false)),
      )),
    ),
    of(false),
  );

  displayedColumns: string[] = [
    'name',
    'province',
    'crop',
    'cropVariety',
    'areaInHectares',
    'soilType',
    'groundwaterPreservation',
    'planetProof',
    'organic',
    'actions',
  ];
  pageSize = 10;

  page$ = new BehaviorSubject<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: 0,
  });

  allCropFields$ = combineLatest([
    this.existingCropFields$,
    this.crops$,
    this.soilTypes$.pipe(filterLoaded()),
    this.cropVarieties$,
  ]).pipe(
    map(([existingCropFields, crops, soilTypes, cropVarietiesByCropId]) => {

      const convertedExistingCrops = existingCropFields.map((cropField) => {
        const crop = crops.find((c) => c.id === cropField.cropId);
        const cropName = crop?.name || '';

        const soilType = soilTypes.find((st) => st.id === cropField.soilTypeId);
        const soilTypeName = soilType?.name || '';

        const cropVarietiesForThisCrop =
          cropVarietiesByCropId?.find((cv) => cv.cropId === cropField.cropId)?.cropVarieties || [];
        const cropVariety = cropVarietiesForThisCrop.find((cv) => cv.id === cropField.cropVarietyId);
        const cropVarietyName = cropVariety?.name || '';

        return {
          id: cropField.id,
          cropId: cropField.cropId,
          cropName,
          areaInHectares: cropField.areaInHectares,
          soilTypeId: cropField.soilTypeId,
          soilTypeName,
          name: cropField.name,
          province: cropField.province || '',
          cropVarietyId: cropField.cropVarietyId,
          cropVarietyName,
          organic: cropField.isSkal || false,
          planetProof: cropField.isPlanetProof || false,
          isParticipatingToBem: (cropField.isParticipatingToBem && cropField.isCancellingBem === false) || false,
          groundwaterPreservation: cropField.isGroundWaterPreservation || false,
        } as ManualCrop;
      });

      const combinedCrops = [...convertedExistingCrops];
      return combinedCrops;
    }),
  );

  cropFields$ = combineLatest([this.allCropFields$, this.page$]).pipe(
    map(([allCropFields, pageEvent]) => {
      const startIndex = pageEvent.pageIndex * pageEvent.pageSize;
      const paginatedItems = allCropFields.slice(startIndex, startIndex + pageEvent.pageSize);
      return paginatedItems;
    }),
  );

  @HostListener('window:beforeunload', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unloadNotification($event: any) {
    if (this.hasUnsavedData) {
      $event.returnValue = true;
    }
  }

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'manualCrops.manualCropsTitle' }));

    this.store.dispatch(loadCrops());
    this.store.select(selectCropYear).pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(loadArableAdviceCropFields({ cropYear, cropFieldType: CropFieldType.Advice }));
    });

    this.page$.next({
      pageIndex: 0,
      pageSize: this.pageSize,
      length: 0,
    });

    this.existingCropFields$.pipe(skip(1), take(1), takeUntil(this.destroy$)).subscribe((cropFields) => {
      if (cropFields && cropFields.length === 0) {
        this.addManualCrop();
      } else {
        const cropIds = cropFields
          .map((field) => field.cropId)
          .filter((id): id is number => id !== undefined && id !== null);

        if (cropIds.length > 0) {
          this.store.dispatch(loadCropVarietiesByCropIdRequest({ cropIds }));
        }
      }
    });

    this.store.dispatch(loadSoilTypesInitiate());
  }

  addManualCrop(): void {
    this.openFormDialog(new ManualCrop());
  }

  upsertManualCrop(manualCrop: ManualCrop): void {
    if (this.isCropYearReadOnly()) return;
    this.openFormDialog(manualCrop);
  }

  deleteManualCrop(cropFieldId: number): void {
    if (this.isCropYearReadOnly() || !cropFieldId) {
      return;
    }

    this.dialog
      .confirm({
        title: this.localeService.translate('manualCrops.deleteConfirmMessage'),
        confirmButtonText: this.localeService.translate('manualCrops.deleteConfirmButton'),
        pushHistoryState: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm && !this.isCropYearReadOnly()) {
          this.isNexusLoadingTrigger$.next();
          this.store.dispatch(deleteCropField({ cropFieldId }));
        }
      });
  }

  page(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.page$.next(event);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  navigateBack(): void {
    this.router.navigate(['/']);
  }

  private openFormDialog(manualCrop: ManualCrop) {
    this.dialog
      .form(
        ManualCropFormComponent,
        {
          manualCrop,
          fullscreen: true,
          pushHistoryState: false,
        },
        {
          minWidth: '30%',
        },
      )
      .pipe(take(1))
      .subscribe(({ status, result }) => {
        if (status === DIALOG_RETURN_TYPES.confirm && result) {
          this.isNexusLoadingTrigger$.next();
          this.store.dispatch(upsertCropField({ cropField: result.manualCrop }));
        }
      });
  }
}
